<template>
  <v-dialog
    :value="show"
    max-width="500"
    content-class="base-export-dialog"
    @input="$emit('update:show', $event)"
  >
    <v-card class="base-export-card">
      <div class="base-export-header">
        <h2 class="base-export-title">Экспорт базы</h2>

        <p class="base-export-subtitle">Выберите данные и формат выгрузки</p>
      </div>

      <div class="base-export-content">
        <div class="base-export-label">Что экспортировать?</div>

        <v-select
          v-model="exportType"
          :items="options"
          :menu-props="{
            contentClass: 'base-export-select-menu',
            offsetY: true,
            maxHeight: 280,
          }"
          item-text="label"
          item-value="value"
          placeholder="Выберите вариант"
          outlined
          dense
          hide-details
          class="base-export-select"
        >
          <template #selection="{ item }">
            <span class="base-export-selection">
              {{ item.label }}
            </span>
          </template>

          <template #item="{ item, on, attrs }">
            <v-list-item v-bind="attrs" class="base-export-menu-item" v-on="on">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.label }}
                </v-list-item-title>

                <v-list-item-subtitle>
                  <template v-if="item.value === 'base'">
                    Контакты выбранной базы
                  </template>

                  <template v-else-if="item.value === 'black'">
                    Номера из чёрного списка
                  </template>

                  <template v-else> База и чёрный список </template>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-select>

        <div v-if="isMultiple" class="base-export-mode">
          <div class="base-export-label">Формат нескольких баз</div>

          <v-radio-group
            v-model="exportMode"
            mandatory
            hide-details
            class="base-export-options"
          >
            <v-radio
              value="separate"
              color="#4B5EFF"
              class="base-export-option"
            >
              <template #label>
                <div class="base-export-option-content">
                  <div class="base-export-option-title">Отдельными файлами</div>

                  <div class="base-export-option-description">
                    Каждая база попадёт в отдельный файл архива
                  </div>
                </div>
              </template>
            </v-radio>

            <v-radio value="merge" color="#4B5EFF" class="base-export-option">
              <template #label>
                <div class="base-export-option-content">
                  <div class="base-export-option-title">
                    Объединить в один файл
                  </div>

                  <div class="base-export-option-description">
                    Все выбранные базы попадут в одну таблицу
                  </div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </div>

        <div v-if="loading" class="base-export-loading">
          <v-progress-circular
            indeterminate
            size="34"
            width="4"
            color="#4B5EFF"
          />

          <div>
            <div class="base-export-loading-title">Формируем файл</div>

            <div class="base-export-loading-description">
              Примерное время: {{ estimatedMinutes }} мин. Не закрывайте окно.
            </div>
          </div>
        </div>
      </div>

      <div class="base-export-actions">
        <v-btn
          text
          class="base-export-cancel"
          :disabled="loading"
          @click="close"
        >
          Отмена
        </v-btn>

        <v-btn
          class="base-export-submit"
          elevation="0"
          :disabled="!exportType || loading"
          @click="exportData"
        >
          Скачать
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    guid: [String, Array],
    show: Boolean,
  },
  data() {
    return {
      exportType: null,
      exportMode: 'separate',
      loading: false,
      options: [
        { label: 'Только база', value: 'base' },
        { label: 'Только чёрный список', value: 'black' },
        { label: 'База и чёрный список', value: 'both' },
      ],
    };
  },
  computed: {
    isMultiple() {
      return Array.isArray(this.guid) && this.guid.length > 1;
    },
    estimatedMinutes() {
      if (!this.isMultiple) return '1';

      const count = this.guid.length;

      if (count <= 2) return '1–2';
      if (count <= 5) return '2–4';
      if (count <= 10) return '4–6';

      return '5+';
    },
  },
  methods: {
    close() {
      this.$emit('update:show', false);
      this.exportType = null;
      this.exportMode = 'separate';
    },

    async exportData() {
      try {
        this.loading = true;
        const token = sessionStorage.token || '';
        const ids = (Array.isArray(this.guid) ? this.guid : [this.guid]).filter(
          Boolean,
        );

        if (!ids.length) {
          throw new Error('Не выбрана база для экспорта');
        }

        const isMultiple = ids.length > 1;

        const payload = isMultiple
          ? { ids, type: this.exportType, token }
          : { id: ids[0], type: this.exportType, token };

        if (isMultiple) {
          payload.mode = this.exportMode;
        }

        /* ── здесь вызываем именно axios, а не post() ── */
        const { data, headers } = await this.$http.axios.post(
          '/base/export',
          payload,
          {
            responseType: 'blob',
          },
        );

        /* ── сохранение файла ── */
        const blob = new Blob([data]);
        const url = URL.createObjectURL(blob);

        let filename;

        if (!isMultiple) {
          const disposition = headers['content-disposition'] || '';
          const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i);
          const plainName = disposition.match(/filename="([^"]+)"/i);

          filename = encodedName
            ? decodeURIComponent(encodedName[1])
            : plainName
            ? plainName[1]
            : `${payload.id}.xlsx`;
        } else if (this.exportMode === 'merge') {
          filename = 'bases.xlsx';
        } else {
          filename = 'bases.zip';
        }

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();

        URL.revokeObjectURL(url);
        this.close();
      } catch (e) {
        console.error(e);
        this.$toast.error('Не удалось экспортировать');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style scoped>
::v-deep .base-export-dialog {
  overflow: hidden;
  border-radius: 18px;
  box-shadow: 0 24px 70px rgba(43, 45, 51, 0.22);
}

::v-deep .base-export-card {
  padding: 30px;
  background: #ffffff;
  border-radius: 18px !important;
}

.base-export-header {
  width: 100%;
  margin-bottom: 26px;
  text-align: center;
}

.base-export-title {
  margin: 0;
  color: #2b2d33;
  font: 700 23px/1.25 Manrope, system-ui, sans-serif;
}

.base-export-subtitle {
  margin: 6px 0 0;
  color: #757575;
  font: 400 14px/1.45 Manrope, system-ui, sans-serif;
}

.base-export-content {
  display: flex;
  flex-direction: column;
}

.base-export-label {
  margin-bottom: 7px;
  color: #4f515a;
  font: 600 13px/1.3 Manrope, system-ui, sans-serif;
}

::v-deep .base-export-select {
  margin: 0;
}

::v-deep .base-export-select .v-input__slot {
  min-height: 48px !important;
  background: #f8f9ff !important;
  border-radius: 10px !important;
}

::v-deep .base-export-select fieldset {
  border-color: #dfe2f2 !important;
}

::v-deep .base-export-select.v-input--is-focused fieldset {
  border-color: #4b5eff !important;
  border-width: 1px !important;
}

.base-export-mode {
  margin-top: 24px;
}

::v-deep .base-export-options {
  margin: 0;
  padding: 0;
}

::v-deep .base-export-options .v-input--radio-group__input {
  gap: 12px;
}

::v-deep .base-export-option {
  box-sizing: border-box;
  width: 100%;
  margin: 0 !important;
  padding: 15px 16px;
  background: #f8f9ff;
  border: 1px solid #e1e4f5;
  border-radius: 12px;
  transition: background-color 0.2s ease, border-color 0.2s ease,
    box-shadow 0.2s ease;
}

::v-deep .base-export-option:hover {
  background: #f2f3ff;
  border-color: #b9c0ff;
}

::v-deep .base-export-option.v-item--active {
  background: #eef0ff;
  border-color: #4b5eff;
  box-shadow: 0 0 0 3px rgba(75, 94, 255, 0.1);
}

::v-deep .base-export-option .v-label {
  flex: 1;
  margin-left: 10px;
}

.base-export-option-content {
  width: 100%;
}

.base-export-option-title {
  color: #2b2d33;
  font: 600 15px/1.3 Manrope, system-ui, sans-serif;
}

.base-export-option-description {
  margin-top: 4px;
  color: #757575;
  font: 400 13px/1.4 Manrope, system-ui, sans-serif;
}

.base-export-loading {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 22px;
  padding: 15px 16px;
  color: #2b2d33;
  background: #eef0ff;
  border: 1px solid #d6daff;
  border-radius: 12px;
}

.base-export-loading-title {
  font-size: 14px;
  font-weight: 600;
}

.base-export-loading-description {
  margin-top: 3px;
  color: #696c76;
  font-size: 12px;
  line-height: 1.4;
}

.base-export-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 28px;
}

::v-deep .base-export-cancel,
::v-deep .base-export-submit {
  min-width: 120px !important;
  height: 42px !important;
  border-radius: 9px !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

::v-deep .base-export-cancel {
  color: #5d606b !important;
}

::v-deep .base-export-submit {
  color: #ffffff !important;
  background: #4b5eff !important;
}

::v-deep .base-export-submit:hover {
  background: #3f50e8 !important;
}

::v-deep .base-export-submit.v-btn--disabled {
  color: #ffffff !important;
  background: #aeb5ff !important;
}

@media (max-width: 540px) {
  ::v-deep .base-export-card {
    padding: 24px 18px;
  }

  .base-export-actions {
    flex-direction: column-reverse;
  }

  ::v-deep .base-export-cancel,
  ::v-deep .base-export-submit {
    width: 100%;
  }
}
::v-deep .base-export-select fieldset {
  border-color: #dfe2f2 !important;
}

::v-deep .base-export-select:hover fieldset {
  border-color: #b9c0ff !important;
}

::v-deep .base-export-select.v-input--is-focused fieldset {
  border-color: #4b5eff !important;
  border-width: 1px !important;
}

::v-deep .base-export-select .v-select__selection {
  max-width: 100%;
}

.base-export-selection {
  overflow: hidden;
  color: #2b2d33;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
<style>
.base-export-select-menu {
  overflow: hidden !important;
  margin-top: 6px;
  padding: 6px;
  background: #ffffff;
  border: 1px solid #e1e4f5;
  border-radius: 12px !important;
  box-shadow: 0 14px 36px rgba(43, 45, 51, 0.18) !important;
}

.base-export-select-menu .v-list {
  padding: 0 !important;
}

.base-export-select-menu .base-export-menu-item {
  min-height: 58px;
  margin: 3px 0;
  padding: 9px 13px;
  border-radius: 8px;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.base-export-select-menu .base-export-menu-item:hover {
  background: #f2f3ff !important;
}

.base-export-select-menu .base-export-menu-item.v-list-item--active {
  color: #4b5eff !important;
  background: #eef0ff !important;
}

.base-export-select-menu .v-list-item__title {
  color: #2b2d33;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
}

.base-export-select-menu .v-list-item__subtitle {
  margin-top: 3px;
  color: #858791;
  font-size: 12px;
  text-align: left;
}

.base-export-select-menu
  .base-export-menu-item.v-list-item--active
  .v-list-item__title {
  color: #4b5eff;
}
</style>
